import { regexForName } from "common/constant";
import { getOptionValue } from "common/enums";
import { isBoolean } from "lodash";
import moment from "moment";
import * as Yup from "yup";

const generalInformationSchema = Yup.object({
  fireSafetyOccupancyTypeId: Yup.string().required(
    "Type of occupancy is required"
  ),
  numberOfBlock: Yup.number()
    .positive("Number must be positive")
    .required("Number of block is required"),
  buildingHeight: Yup.string().required("Building height is required"),
  mainEntranceWidth: Yup.string().required("Main entrance width is required"),
  totalFloorArea: Yup.string().required("Total floor area is required"),
  buildUpArea: Yup.string().required("Build up area is required"),
  frontClearSpace: Yup.string().required("Front space is required"),
  backClearSpace: Yup.string().required("Back space is required"),
  rightClearSpace: Yup.string().required("Right space is required"),
  leftClearSpace: Yup.string().required("Left space is required"),

  authority: Yup.object({
    buildingAuthorityRoleId: Yup.string().required(
      "Building authority is required"
    ),
    name: Yup.string()
      .matches(regexForName, "Please enter a valid name")
      .required("Name is required"),
    mobileNumber: Yup.string()
      .matches(/^\d{10}$/, "Please enter valid mobile number")
      .required("Mobile number is required"),
    email: Yup.string()
      .email("Please enter valid email")
      .required("Email is required"),
  }),

  buildingAuthority: Yup.object({
    buildingAuthorityRoleId: Yup.string().required(
      "Building authority is required"
    ),
    name: Yup.string()
      .matches(regexForName, "Please enter a valid name")
      .required("Name is required"),
    mobileNumber: Yup.string()
      .matches(/^\d{10}$/, "Please enter valid mobile number")
      .required("Mobile number is required"),
    email: Yup.string()
      .email("Please enter valid email")
      .required("Email is required"),
  }),

  responsiblePersonAuthorities: Yup.array().of(
    Yup.object({
      name: Yup.string()
        .nullable()
        .matches(regexForName, "Please enter a valid name")
        .required("Name is required"),
      mobileNumber: Yup.string()
        .nullable()
        .matches(/^\d{10}$/, "Please enter valid mobile number")
        .required("Mobile number is required"),
      designation: Yup.string().nullable().required("Designation is required"),
      email: Yup.string()
        .nullable()
        .email("Please enter valid email")
        .required("Email is required"),
    })
  ),

  fireSafetyAddress: Yup.object({
    buildingName: Yup.string().required("Building name is required"),
    buildingId: Yup.string(),
    blockName: Yup.string().required("Block No is required"),
    premisesAreaId: Yup.string().required("Area is required"),
    zoneName: Yup.string().required("Zone is required"),
    floors: Yup.array().min(1, "Floors is required"),
    otherZone: Yup.string().test("isOtherZone", "", function () {
      const [{ value: current = null } = {}] = this.from || [];

      if (current.zoneName === "other" && !current.otherZone) {
        return this.createError({
          message: "Zone is required",
        });
      }
      return true;
    }),
    otherBlock: Yup.string().test("isOtherBlock", "", function () {
      const [{ value: current = null } = {}] = this.from || [];

      if (current.blockName === "other" && !current.otherBlock) {
        return this.createError({
          message: "Block is required",
        });
      }
      return true;
    }),
    otherBuilding: Yup.string().test("isOtherBuilding", "", function () {
      const [{ value: current = null } = {}] = this.from || [];

      if (current.buildingName === "other" && !current.otherBuilding) {
        return this.createError({
          message: "Building is required",
        });
      }
      return true;
    }),
    pinCode: Yup.string()
      .matches(/^\d{6}$/, "Please enter valid pincode")
      .required("Pincode is required"),
  }),
  fireSafetyCertificate: Yup.object({
    approvalNumber: Yup.string().required("FSCA no is required"),
    dateOfIssue: Yup.date()
      .max(moment(), "Enter valid date of issue")
      .required("Date of issue is required"),
    dueDate: Yup.date().test("dateOfIssue", "", function () {
      const [{ value: { dueDate, dateOfIssue } = {} } = {}] = this.from || [];
      if (!dueDate) {
        return this.createError({ message: "Due date is required" });
      }
      if (dueDate && !dueDate?.isValid()) {
        return this.createError({
          message: "Enter a valid date",
        });
      }
      if (dateOfIssue && dueDate?.isSameOrBefore(dateOfIssue)) {
        return this.createError({ message: "Enter valid due date" });
      }
      return true;
    }),
  }),
  floorWiseTenants: Yup.array().of(
    Yup.object().shape({
      floorId: Yup.string().nullable().required("Floor number is required"),
      name: Yup.string()
        .nullable()
        .matches(regexForName, "Please enter a valid name")
        .required("Tenant name is required"),
      nameOfResponsiblePerson: Yup.string()
        .nullable()
        .required("Responsible person name is required"),
      designation: Yup.string().nullable().required("Designation is required"),
      mobileNumber: Yup.string()
        .nullable()
        .matches(/^\d{10}$/, "Please enter valid mobile number")
        .required("Mobile number is required"),
      email: Yup.string()
        .nullable()
        .email("Please enter valid email")
        .required("Email is required"),
    })
  ),
});

const utilityAndMechanicalSystemSchema = Yup.object({
  utilityMechanicalSystem: Yup.object({
    isServiceDuct: Yup.boolean().required("This field is required"),
    isServiceDuctsEnclosed: Yup.boolean().required("This field is required"),
    numberOfPassengerLift: Yup.string().required(
      "Number of passenger lift is required"
    ),
    numberOfFireLift: Yup.string().required("Number of fire lift is required"),
    isAutomaticRescueDeviceForLift: Yup.boolean().required(
      "This field is required"
    ),
    numberOfNaturalVentilation: Yup.number().required(
      "Number of natural ventilation is required"
    ),
    numberOfMechanicalVentilation: Yup.number().required(
      "Number of mechanical ventilation is required"
    ),
    electricalSystem: Yup.object({
      isElectricSubstation: Yup.boolean().required("This field is required"),
      isTransformerForBuildingBlock: Yup.boolean().required(
        "This field is required"
      ),
      numberOfTransformers: Yup.string().when("isTransformerForBuildingBlock", {
        is: true,
        then: () =>
          Yup.number()
            .positive("Number of transformer must be positive")
            .required("Number of transformer is required"),
      }),
      transformerType: Yup.string().when("isTransformerForBuildingBlock", {
        is: true,
        then: () => Yup.string().required("Transformer type is required"),
      }),
      transformerCapacity: Yup.string().when("isTransformerForBuildingBlock", {
        is: true,
        then: () =>
          Yup.number()
            .positive("Transformer capacity must be positive")
            .required("Transformer capacity is required"),
      }),
      transformerLocation: Yup.string().when("isTransformerForBuildingBlock", {
        is: true,
        then: () => Yup.string().required("Transformer location is required"),
      }),

      typeOfFireProtectionSystemForTransformer: Yup.string(),
      isSubstation: Yup.boolean().required("This field is required"),
      isStandbyPower: Yup.boolean().required("This field is required"),
      isStandbyPowerAutomatic: Yup.boolean().required("This field is required"),
      standbyPowerLocation: Yup.string().when("isStandbyPowerAutomatic", {
        is: (isStandbyPowerAutomatic) => !!isStandbyPowerAutomatic,
        then: () =>
          Yup.string().required("Power or generator Location is required"),
      }),
      isUseOfRCCBELCB: Yup.boolean().required("This field is required"),
      isEarthing: Yup.boolean().required("This field is required"),
      islighteningProtectionSystem: Yup.boolean().required(
        "This field is required"
      ),
    }),
  }),
});

const fireProtectionSchema = Yup.object({
  fireProtection: Yup.object({
    underGroundTankCapacity: Yup.number().required(
      "Underground tank capacity is required"
    ),
    overHeadTankCapacity: Yup.number().required(
      "Overhead tank capacity is required"
    ),
    firePumpLocation: Yup.string().required("Fire pump location is required"),
    isFirstAidHoseReelProvided: Yup.boolean().required(
      "This field is required"
    ),
    numberOfFirstAidHoseReel: Yup.string().when("isFirstAidHoseReelProvided", {
      is: true,
      then: () =>
        Yup.number()
          .positive("Number must be positive")
          .required("Number of first aid hose reel is required"),
    }),
    isWetControllerSystemProvided: Yup.boolean().required(
      "This field is required"
    ),
    numberOfWetControllerSystem: Yup.string().when(
      "isWetControllerSystemProvided",
      {
        is: true,
        then: () =>
          Yup.number()
            .positive("Number must be positive")
            .required("Number of wet controller system is required"),
      }
    ),
    isInternalHydrantProvided: Yup.boolean().required("This field is required"),
    numberOfInternalHydrant: Yup.string().when("isInternalHydrantProvided", {
      is: true,
      then: () =>
        Yup.number()
          .positive("Number must be positive")
          .required("Number of internal hydrant is required"),
    }),
    isYardHydrantProvided: Yup.boolean().required("This field is required"),
    numberOfYardHydrant: Yup.string().when("isYardHydrantProvided", {
      is: true,
      then: () =>
        Yup.number()
          .positive("Number must be positive")
          .required("Number of yard hydrant is required"),
    }),
    isBasementHasSprinklerSystem: Yup.boolean().required(
      "This field is required"
    ),
    basementSprinklers: Yup.array().of(
      Yup.object().shape({
        sprinklerType: Yup.string()
          .nullable()
          .required("Sprinkler type is required"),
        floorId: Yup.string().nullable().required("Floor is required"),
        quantity: Yup.number()
          .nullable()
          .positive("Quantity must be greater than 0")
          .required("Quantity is required"),
      })
    ),
    isGroundFloorHasSprinklerSystem: Yup.boolean().required(
      "This field is required"
    ),
    groundFloorSprinklerSystemQuantity: Yup.string().when(
      "isGroundFloorHasSprinklerSystem",
      {
        is: true,
        then: () =>
          Yup.number()
            .positive("Quantity must be positive")
            .required("Ground Floor quantity is required"),
      }
    ),
    isHollowPlinthHasSprinklerSystem: Yup.boolean().required(
      "This field is required"
    ),
    hollowPlinthSprinklerSystemQuantity: Yup.string().when(
      "isHollowPlinthHasSprinklerSystem",
      {
        is: true,
        then: () =>
          Yup.number()
            .positive("Quantity must be positive")
            .required("Hollow Plinth quantity is required"),
      }
    ),
    isFloorHasSprinklerSystem: Yup.boolean().required("This field is required"),
    floorSprinklers: Yup.array().of(
      Yup.object().shape({
        sprinklerType: Yup.string()
          .nullable()
          .required("Sprinkler type is required"),
        floorId: Yup.string().nullable().required("Floor is required"),
        quantity: Yup.number()
          .nullable()
          .positive("Quantity must be greater than 0")
          .required("Quantity is required"),
      })
    ),
    detectorSystems: Yup.array().of(
      Yup.object().shape({
        floorId: Yup.string().nullable().required("Floor is required"),
        operationType: Yup.string()
          .nullable()
          .required("Operation type is required"),
        detectionTypes: Yup.array().min(1, "Detection types is required"),
        makeForOther: Yup.string()
          .nullable()
          .test("isMakeForOtherRequired", "", function () {
            const [{ value: current = null } = {}] = this.from || [];
            if (
              current.detectionTypes.some(
                ({ value }) =>
                  value !==
                  getOptionValue(
                    "fireDetectionTypeEnum",
                    "Gas Leak Detection System"
                  )
              ) &&
              !current.makeForOther
            ) {
              return this.createError({
                message: "Make is required",
              });
            }
            return true;
          }),
        makeForGasLeak: Yup.string()
          .nullable()
          .test("isMakeForGasLeak", "", function () {
            const [{ value: current = null } = {}] = this.from || [];
            if (
              current.detectionTypes.some(
                ({ value }) =>
                  value ===
                  getOptionValue(
                    "fireDetectionTypeEnum",
                    "Gas Leak Detection System"
                  )
              ) &&
              !current.makeForGasLeak
            ) {
              return this.createError({
                message: "Make for gas leak is required",
              });
            }
            return true;
          }),
      })
    ),
    location: Yup.string().required("Location is required"),
    isConfiguredInIBMS: Yup.boolean().required("This field is required"),
    isInstalledInTenantArea: Yup.boolean().required("This field is required"),
    isIntegratedWithTenantAccessControl: Yup.boolean().required(
      "This field is required"
    ),
    isIntegratedWithMainSystem: Yup.boolean().required(
      "This field is required"
    ),
    isIntegratedWithBuildingSystem: Yup.boolean().required(
      "This field is required"
    ),
    isIntegratedWithCommandCenter: Yup.boolean().required(
      "This field is required"
    ),
    causeAndEffectMatrixDocument: Yup.string().test(
      "isDocumentUrlAvailable",
      "",
      function () {
        const [{ value: current = null } = {}] = this.from || [];
        if (
          !current.causeAndEffectMatrixDocumentUrl &&
          !current.causeAndEffectMatrixDocument
        ) {
          return this.createError({
            message: "Cause and Effect Matrix document is required",
          });
        }
        return true;
      }
    ),

    fireFightingPumps: Yup.array().of(
      Yup.object({
        type: Yup.string().nullable().required("Pump type is required"),
        numberOfPump: Yup.number()
          .positive("Number of pump must be positive")
          .nullable()
          .required("Number of pump is required"),
        head: Yup.string().nullable().required("Head is required"),
        discharge: Yup.string().nullable().required("Discharge is required"),
        autoStartPressure: Yup.string()
          .nullable()
          .required("AutoStartPressure is required"),
        autoStopPressure: Yup.string()
          .nullable()
          .required("AutoStopPressure is required"),
        kwOrHp: Yup.string().nullable().required("KW/HP is required"),
        pumpBuildType: Yup.string()
          .nullable()
          .required("Pump build type is required"),
      })
    ),
    floorWiseFireExtinguishers: Yup.array().of(
      Yup.object({
        floors: Yup.array().min(1, "Floors is required"),
        extinguisherType: Yup.string()
          .nullable()
          .required("Fire extinguisher type is required"),
        capacity: Yup.number()
          .positive("Capacity must be positive")
          .nullable()
          .required("Capacity is required"),
        quantity: Yup.number()
          .positive("Quantity must be positive")
          .nullable()
          .required("Quantity is required"),
      })
    ),
  }),
});

const fireProtectionEquipmentSchema = Yup.object({
  floorWiseFireProtectionEquipments: Yup.array().of(
    Yup.object().shape({
      floors: Yup.array().min(1, "Floors is required"),
      hydrantValve: Yup.string()
        .nullable()
        .required("Hydrant valve is required"),
      hoseBox: Yup.string().nullable().required("Hose box is required"),
      hosePipe: Yup.string().nullable().required("Hose pipe is required"),
      branchPipe: Yup.string().nullable().required("Branch pipe is required"),
      hoseReel: Yup.string().nullable().required("Hose reel hose is required"),

      signages: Yup.string().nullable().required("Signages is required"),
      fireExtinguishers: Yup.string()
        .nullable()
        .required("Fire Extinguishers is required"),
      sprinklers: Yup.string().nullable().required("Sprinklers is required"),
      pumpOnOffSwitch: Yup.string()
        .nullable()
        .required("Pump switch is required"),
    })
  ),

  fireProtection: Yup.object({
    isEvacuationPlan: Yup.boolean().required("This field is required"),
    isPublicAddressingSystem: Yup.boolean().required("This field is required"),
    isPublicAddressingSystemInTenantArea: Yup.boolean().when(
      "isPublicAddressingSystem",
      {
        is: true,
        then: () => Yup.string().required("This field is required"),
      }
    ),
    isExternalLifts: Yup.boolean().required("This field is required"),
  }),
});

const fireLifeSafetySchema = Yup.object({
  lifeSafety: Yup.object({
    isCompartmentationDone: Yup.boolean().required("This field is required"),
    isLightningArrestorInstalled: Yup.boolean().required(
      "This field is required"
    ),
    numberOfInternalStaircases: Yup.number()
      .positive("Number of internal staircase must be positive")
      .required("Number of internal staircase is required"),
    internalStaircaseWidth: Yup.string().required(
      "Internal Staircase Width is required"
    ),
    numberOfExternalStaircases: Yup.number().required(
      "Number of external staircase is required"
    ),
    isFireExitDoors: Yup.boolean().required("This field is required"),
    isBasements: Yup.boolean().required("This field is required"),
    numberOfBasements: Yup.string().test("isBasements", "", function () {
      const [{ value: current = null } = {}] = this.from || [];
      if (
        current.isBasements &&
        !current.numberOfBasements &&
        !(current.numberOfBasements >= 0)
      ) {
        return this.createError({ message: "Number of basements is required" });
      }
      return true;
    }),
    isBasementHasSprinklers: Yup.boolean().test("isBasements", "", function () {
      const [{ value: current = null } = {}] = this.from || [];
      if (current.isBasements && !isBoolean(current.isBasementHasSprinklers)) {
        return this.createError({ message: "This field is required" });
      }
      return true;
    }),
    basementVentilationType: Yup.string().test("isBasements", "", function () {
      const [{ value: current = null } = {}] = this.from || [];
      if (current.isBasements && !current.basementVentilationType) {
        return this.createError({
          message: "Basement ventilation type is required",
        });
      }
      return true;
    }),
    isQualifiedFireOfficer: Yup.boolean().required("This field is required"),
    fireOfficerName: Yup.string().when("isQualifiedFireOfficer", {
      is: true,
      then: () => Yup.string().required("Name is required"),
    }),
    fireOfficerMobileNumber: Yup.string().when("isQualifiedFireOfficer", {
      is: true,
      then: () =>
        Yup.string()
          .matches(/^\d{10}$/, "Please enter valid mobile number")
          .required("Mobile number is required"),
    }),
    fireOfficerEmailId: Yup.string().when("isQualifiedFireOfficer", {
      is: true,
      then: () =>
        Yup.string()
          .email("Please enter valid email")
          .required("Email is required"),
    }),
    isAlternatePowerSupply: Yup.boolean().required("This field is required"),
    isExitSigns: Yup.boolean().required("This field is required"),
    assemblyPointDetail: Yup.string(),
    dbrDocuments: Yup.array().min(1, "Document is required"),
    refugeAreas: Yup.array().of(
      Yup.object().shape({
        floors: Yup.array().min(1, "Floors is required"),
        area: Yup.string().nullable().required("Area is required"),
      })
    ),
  }),
});

export const getRegisterBuildingValidation = {
  0: generalInformationSchema,
  1: utilityAndMechanicalSystemSchema,
  2: fireProtectionSchema,
  3: fireProtectionEquipmentSchema,
  4: fireLifeSafetySchema,
};
